import { LINKS } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/links';
import { Screen } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/payment-complete-screen';
import type { WishlistItem } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/types';
import { useAuth } from '@fans-fund-me/shared-common-esm-authentication';
import { useCurrency } from '@fans-fund-me/shared-common-esm-currency';
import { useForm } from '@mantine/form';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { createSurpriseGiftPayment, updateSurpriseGiftPayment } from '../../api/payments.ts';
import { getProfile, getRandomCreator, getWishlist } from '../../api/profile.ts';
import { FORM_ERRORS } from '../../constants/form_errors.ts';

function ElementsWrapper() {
  const [stripePromise] = useState(() => loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY!));

  const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

  if (clientSecret === '') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return <FFM.PaymentComplete.Screen isLoading={true} />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: clientSecret!,
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Work+Sans',
          },
        ],
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#af8100',
            colorBackground: '#212121',
            spacingUnit: '0.25rem',
            fontFamily: 'Work Sans',
            borderRadius: '0.25rem',
            spacingAccordionItem: '1rem',
            spacingGridColumn: '2rem',
            spacingGridRow: '2rem',
          },
          rules: {
            '.AccordionItem': {
              boxShadow: 'none',
              border: 'none',
            },
            '.Input': {
              borderColor: '#5d5d5d',
              backgroundColor: '#2f3032',
              boxShadow: '0',
            },
            '.Block': {
              boxShadow: 'none',
              paddingTop: '1rem',
              paddingBottom: '1.5rem',
            },
          },
        },
      }}
    >
      <PaymentComplete clientSecret={clientSecret!} />
    </Elements>
  );
}

function PaymentComplete({ clientSecret }: { clientSecret: string }) {
  const navigate = useNavigate();
  const stripe = useStripe();
  const currency = useCurrency();
  const auth = useAuth();

  // const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

  const [creatorId, setCreatorId] = useState('');
  const [creatorName, setCreatorName] = useState('');
  const [creatorUsername, setCreatorUsername] = useState('');
  const [creatorProfilePicture, setCreatorProfilePicture] = useState('');
  const [creatorSurpriseGiftEnabled, setCreatorSurpriseGiftEnabled] = useState(false);
  const [creatorCurrency, setCreatorCurrency] = useState('');
  const [wishlistItems, setWishlistItems] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isErrored, setIsErrored] = useState(false);
  const [isRandomCreatorLoading, setIsRandomCreatorLoading] = useState(false);

  const [surpriseGiftId, setSurpriseGiftId] = useState('');
  const [surpriseGiftExchangeRate, setSurpriseGiftExchangeRate] = useState('');
  const [surpriseGiftConvertedAmount, setSurpriseGiftConvertedAmount] = useState('');

  useEffect(() => {
    const userID = localStorage.getItem('ffm_checkout_user');
    const redirectUsername = localStorage.getItem('ffm_payment_complete_redirect_to');

    if (userID === null && redirectUsername !== null) {
      localStorage.removeItem('ffm_payment_complete_redirect_to');
      navigate(`/${redirectUsername}`);
    }

    getProfile(userID!, '')
      .then((response) => {
        setCreatorId(response.data.userId);
        setCreatorName(response.data.displayName);
        setCreatorUsername(response.data.username);
        setCreatorProfilePicture(response.data.profilePic);
        setCreatorCurrency(response.data.currency);
        setCreatorSurpriseGiftEnabled(response.data.enableSurpriseGifts);

        localStorage.setItem('ffm_payment_complete_redirect_to', response.data.username);
      })
      .catch((error) => {
        console.error('Error getting wishlist user profile', JSON.stringify(error));
        navigate(LINKS.NOT_FOUND);
      });
  }, []);

  useEffect(() => {
    const userID = localStorage.getItem('ffm_checkout_user');
    if (currency.value !== '' && currency.value !== undefined && currency.value !== null) {
      getWishlist(userID!, currency.value).then((response) => {
        setWishlistItems(response.data.items);
      });
    }
  }, [currency.value]);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (clientSecret !== null) {
      stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
        if (paymentIntent !== undefined) {
          // Inspect the PaymentIntent `status` to indicate the status of the payment
          // to your customer.
          //
          // Some payment methods will [immediately succeed or fail][0] upon
          // confirmation, while others will first enter a `processing` state.
          //
          // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
          switch (paymentIntent.status) {
            case 'succeeded':
              setIsLoading(false);
              setIsErrored(false);

              // localStorage.removeItem('ffm_checkout_user');
              localStorage.removeItem('ffm_checkout_item');
              localStorage.removeItem('ffm_checkout_surprise_gift');
              localStorage.removeItem('ffm_checkout_surprise_gift_amount');
              localStorage.removeItem('ffm_checkout_surprise_gift_id');
              localStorage.removeItem('ffm_checkout_surprise_gift_message');
              break;

            case 'processing':
              setIsLoading(true);
              break;

            case 'requires_payment_method':
              console.error('Payment intent status is', paymentIntent.status);
              setIsErrored(true);
              break;

            default:
              console.error('Payment intent status is', paymentIntent.status);
              setIsErrored(true);
              break;
          }
        }
      });
    }
  }, [stripe, clientSecret]);

  const handleClickWishlistItem = (item: WishlistItem) => {
    localStorage.setItem('ffm_checkout_user', creatorId);
    localStorage.setItem('ffm_checkout_item', item.id);
    navigate(LINKS.CHECKOUT);
  };

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      amount: '',
      reason: '',
    },
    validate: {
      amount: (value) =>
        value === ''
          ? FORM_ERRORS.AMOUNT_REQUIRED
          : !new RegExp('^\\d{1,8}(\\.\\d{1,2})?$').test(value)
            ? FORM_ERRORS.MUST_BE_CURRENCY
            : null,
    },
  });

  const handleSubmitSurpriseGift = (values: typeof form.values) => {
    updateSurpriseGiftPayment(
      { id: surpriseGiftId, suggestion: values.reason, senderAmount: values.amount },
      surpriseGiftId,
      auth.token,
    )
      .then((response) => {
        const paymentId = response.data.singlePaymentId;

        localStorage.removeItem('ffm_checkout_user');
        localStorage.removeItem('ffm_checkout_item');
        localStorage.removeItem('ffm_checkout_surprise_gift');
        localStorage.removeItem('ffm_checkout_surprise_gift_amount');
        localStorage.removeItem('ffm_checkout_surprise_gift_id');
        localStorage.removeItem('ffm_checkout_surprise_gift_payment_id');
        localStorage.removeItem('ffm_checkout_surprise_gift_message');

        localStorage.setItem('ffm_checkout_user', creatorId);
        localStorage.setItem('ffm_checkout_username', creatorUsername);
        localStorage.setItem('ffm_checkout_surprise_gift', 'true');
        localStorage.setItem('ffm_checkout_surprise_gift_id', surpriseGiftId);
        localStorage.setItem('ffm_checkout_surprise_gift_payment_id', paymentId);
        localStorage.setItem('ffm_checkout_surprise_gift_amount', values.amount);
        localStorage.setItem('ffm_checkout_surprise_gift_message', values.reason);
        // old
        // localStorage.setItem('ffm_checkout_surprise_gift', 'true');
        // localStorage.setItem('ffm_checkout_surprise_gift_amount', values.amount);
        // localStorage.setItem('ffm_checkout_surprise_gift_id', creatorId);
        // localStorage.setItem('ffm_checkout_surprise_gift_message', values.reason);
        form.reset();
        navigate(LINKS.CHECKOUT);
      })
      .catch((error) => {
        form.setErrors({
          amount: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'senderAmount',
          )?.message,
        });
      });
  };

  const handleClickRandomCreator = () => {
    setIsRandomCreatorLoading(true);
    localStorage.removeItem('ffm_payment_complete_redirect_to');
    getRandomCreator()
      .then((response) => {
        navigate(`/${response.data.username}`);
      })
      .catch((error) => console.error('Error getting random creator', JSON.stringify(error)));
  };

  const handleClickRetryPayment = () => {
    navigate(LINKS.CHECKOUT);
  };

  useEffect(() => {
    if (creatorSurpriseGiftEnabled && wishlistItems.length === 0) {
      createSurpriseGiftPayment(
        {
          receiverId: creatorId,
          currency: currency.value,
        },
        auth.token,
      )
        .then((response) => {
          setSurpriseGiftId(response.data.id);
          setSurpriseGiftExchangeRate(response.data.exchangeRate);
        })
        .catch((error) => console.error('Error creating surprise gift', JSON.stringify(error)));
    }
  }, [auth.token, creatorId, creatorSurpriseGiftEnabled, currency.value]);

  form.watch('amount', ({ value }) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: creatorCurrency,
    });
    const convertedAmount = Number(surpriseGiftExchangeRate) * Number(value || 0);
    setSurpriseGiftConvertedAmount(formatter.format(convertedAmount));
  });

  return (
    <Screen
      profilePicture={creatorProfilePicture}
      displayName={creatorName}
      isSurpriseGiftEnabled={creatorSurpriseGiftEnabled}
      isLoading={isLoading}
      wishlistItems={wishlistItems}
      handleClickWishlistItem={handleClickWishlistItem}
      form={form}
      handleSubmitSurpriseGift={handleSubmitSurpriseGift}
      handleClickRandomCreator={handleClickRandomCreator}
      isRandomCreatorLoading={isRandomCreatorLoading}
      error={isErrored}
      handleClickRetryPayment={handleClickRetryPayment}
      profileCurrency={creatorCurrency}
      convertedSurpriseGiftAmount={surpriseGiftConvertedAmount}
    />
  );
}

export default ElementsWrapper;
